import {Component, OnInit, ViewChild} from '@angular/core';
import {map, takeUntil} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {ActivatedRoute} from '@angular/router';
import {ProtocolAdminService} from '../../services/api/protocol-admin.service';
import {SerialCodeTableDataViewModel} from '../../viewmodels/serialCodeTableDataViewModel';
import {AccountAdminService} from '../../services/api/account-admin.service';
import {AdminNavigationService} from '../../services/admin-navigation.service';
import {AdminReportGroupFilter} from '../../viewmodels/adminReportGroupFilter';
import {BaseComponent} from '../base.component';
import {LoadingState, NotificationService} from 'common';
import {MatDialog} from '@angular/material/dialog';
import {ProtocolDetailsBySerialCodeDto} from '../../dtos/protocolDetailsBySerialCodeDto';
import {ProtocolActivityDetailsBySerialCodeDataWrapper} from '../../wrappers/protocolActivityDetailsBySerialCodeDataWrapper';
import {ProtocolActivityDetailsBySerialCodeViewModel} from '../../viewmodels/protocolActivityDetailsBySerialCodeViewModel';
import {TableCellInfoWrapper} from '../../wrappers/tableCellInfoWrapper';
import {ProtocolService} from '../../../../../connect/src/app/services/api/protocol.service';
import {UnpackProtocolActivityRepMeasurementDto} from '../../dtos/unpackProtocolActivityRepMeasurementDto';
import {getTitle} from '../../utils/activity-title-utils';
import {removeWhitespaces} from '../../utils/admin-utils';
import {DateFormat, Dates, TextUtils} from 'common';
import {ActivitySensorDto} from '../../dtos/activitySensorDto';

@Component({
    selector: 'app-serial-code',
    templateUrl: './find-sensor-activity-history.component.html',
    styleUrls: ['./find-sensor-activity-history.component.scss']
})
export class FindSensorActivityHistoryComponent extends BaseComponent implements OnInit {
    private ALL_FILTER = 'ALL';

    public showSearch: boolean;
    public readonly searchedDataSource: MatTableDataSource<ProtocolActivityDetailsBySerialCodeViewModel>;
    public readonly showingDataSource: MatTableDataSource<ProtocolActivityDetailsBySerialCodeViewModel>;
    public columns: Map<string, string>;
    public loadingState: LoadingState;
    public viewModel: SerialCodeTableDataViewModel;
    public filter: AdminReportGroupFilter;
    public relatedEmailsArray: string[];
    public filterResultsWithEmail: string;

    @ViewChild(MatSort)
    public sort: MatSort;

    public searchValue: string;
    public querySearchValue: string;
    public searchHint: string;
    public readonly minLength = 6;

    constructor(private protocolAdminService: ProtocolAdminService,
                private accountAdminService: AccountAdminService,
                private notificationService: NotificationService,
                private protocolService: ProtocolService,
                private route: ActivatedRoute,
                public dialog: MatDialog,
                private adminNavigation: AdminNavigationService
    ) {
        super();
        this.searchedDataSource = new MatTableDataSource([]);
        this.showingDataSource = new MatTableDataSource([]);
        this.searchValue = '';
        this.searchHint = 'Insert a serial code to find sensor activity history';
        this.showSearch = true;
        this.relatedEmailsArray = [this.ALL_FILTER];
        this.filterResultsWithEmail = this.ALL_FILTER;

        this.columns = new Map<string, string>();
        this.columns.set('position', 'No');
        this.columns.set('email', 'Email');
        this.columns.set('startTimeDetailedDate', 'Date');
        this.columns.set('title', 'Exercise');
        this.columns.set('exerciseType', 'Type');
        this.columns.set('duration', 'Duration');
        this.columns.set('repetition', 'Repetitions');
        this.columns.set('sensors', 'Sensors (Firmware)');
        this.columns.set('appVersion', 'App Version');
        this.columns.set('downloadRawDataAction', 'Raw Sensor Data');

        this.filter = new AdminReportGroupFilter();
        this.filter.supportsQuery = false;
        this.filter.supportsSubscriptionPlanTypes = false;
        this.filter.supportsSubscriptionPeriodUnits = false;
        this.filter.supportsSubscriptionSources = false;
    }

    ngOnInit(): void {
        this.analyticsSwitchPage('FindSensorActivityHistory', '', '/admin/findSensorActivityHistory');

        this.route.queryParams
            .pipe(takeUntil(this.destroySubject))
            .subscribe(params => {
                    if (!params.sensor) {
                        return;
                    }
                    this.searchValue = params.sensor;
                    this.querySearchValue = params.sensor;
                    this.search(true);
                }
            );
    }

    onSubmit(): void {
        this.search();
    }

    onValueChanged(value: string): void {
        const cleanValue = removeWhitespaces(value);

        this.searchValue = cleanValue.toUpperCase();
    }

    canSearch(): boolean {
        return this.showSearch && this.searchValue?.length >= this.minLength;
    }

    private search(fromInit?: boolean): void {
        if (!fromInit && this.querySearchValue !== this.searchValue) {
            this.adminNavigation.goToFindSensorActivityHistory(this.searchValue);
            return;
        }

        this.loadingState = LoadingState.LOADING;
        this.showSearch = false;
        this.relatedEmailsArray = [this.ALL_FILTER];
        this.filterResultsWithEmail = this.ALL_FILTER;

        this.protocolAdminService.getProtocolActivityDetailsBySerialCode(this.searchValue)
            .pipe(takeUntil(this.destroySubject))
            .pipe(map((protocolDetailsDtos: ProtocolDetailsBySerialCodeDto[]) => {
                const dataWrappers: ProtocolActivityDetailsBySerialCodeDataWrapper[] = [];

                let position = 1;
                protocolDetailsDtos.map(protocolDto => {
                    protocolDto.activities.map(activityDto => {
                        const exerciseType = this.formatWord(activityDto.exerciseType);
                        const title = getTitle(activityDto.title);

                        const wrapper = new ProtocolActivityDetailsBySerialCodeDataWrapper(position, protocolDto.protocolCode, activityDto.code, protocolDto.accountCode,
                            protocolDto.email, activityDto.startTime, title, exerciseType, activityDto.duration, activityDto.repetition,
                            this.formatSensors(activityDto.sensors), this.formatAppVersions(activityDto.appVersions));

                        dataWrappers.push(wrapper);
                        position++;
                    });

                    if (!this.relatedEmailsArray.find(email => protocolDto.email === email)) {
                        this.relatedEmailsArray.push(protocolDto.email);
                    }
                });

                return dataWrappers;
            }))
            .subscribe(results => {
                this.searchedDataSource.data = ProtocolActivityDetailsBySerialCodeViewModel.from(results);
                this.showingDataSource.data = this.searchedDataSource.data;

                this.loadingState = LoadingState.LOADED;
                this.showSearch = true;
            }, error => {
                this.loadingState = LoadingState.ERROR;
                this.showSearch = true;
                this.notification.info(`Please provide a valid serial code.`);
            });
    }

    public onEmailToFilterResultsClicked(email: string): void {
        this.filterResultsWithEmail = email;

        this.loadingState = LoadingState.LOADING;

        if (email === this.ALL_FILTER) {
            this.showingDataSource.data = this.searchedDataSource.data;
        } else {
            this.showingDataSource.data = this.searchedDataSource.data.filter(model => model.email === this.filterResultsWithEmail);
        }

        this.loadingState = LoadingState.LOADED;
    }

    public shouldShowRelatedEmails(): boolean {
        return this.relatedEmailsArray.length > 1;
    }

    public onDownloadRawDataCsvPressed(row: TableCellInfoWrapper): void {
        const protocolActivityDetails = this.searchedDataSource.data.find(viewModel => viewModel.position === row.position);
        const protocolCode = protocolActivityDetails.protocolCode;

        const startTime = protocolActivityDetails.startTimeDetailedDate;
        const title = protocolActivityDetails.title;
        const activityCode = protocolActivityDetails.activityCode;

        this.protocolAdminService.unpackProtocol(protocolCode)
            .pipe(takeUntil(this.destroySubject))
            .subscribe(result => {
                const measurementDtos: UnpackProtocolActivityRepMeasurementDto[] = result.activityReps.filter(activityReps => activityReps.activityCode === activityCode)
                    .flatMap(rep => rep.measurements);
                this.downloadCSV(measurementDtos, startTime, title);
            });
    }

    downloadCSV(unpackedMeasurements: UnpackProtocolActivityRepMeasurementDto[], startTime: number, title: string): void {
        const csvData = this.generateCSV(unpackedMeasurements);
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);

        const titleWithUnderscores = title.replace(/\s/g, '_');
        const downloadFileTitle = this.searchValue + '-' + Dates.format(new Date(startTime), DateFormat.DATE_TIME_HOURS_MINUTES_SECONDS_CSV) + '-' + titleWithUnderscores + '.csv';
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFileTitle;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

    }

    generateCSV(repetitions: UnpackProtocolActivityRepMeasurementDto[]): string {
        let csvContent = '';

        csvContent = `Measurement,Device Type,Body Part,Body Part Side,Deltas,Channel 1, Channel 2, Channel 3, Channel 4,Channel 5, Channel 6, Channel 7, Channel 8, Channel 9, Channel 10, Channel 11, Channel 12, Channel 13, Channel 14, Channel 15, Channel 16, Channel 17\n`;

        repetitions.forEach((repetition, repetitionIndex) => {
            const deltas = repetition.deltas;
            const channel1 = repetition.channel1Forces;
            const channel2 = repetition.channel2Forces;
            const channel3 = repetition.channel3Forces;
            const channel4 = repetition.channel4Forces;
            const channel5 = repetition.channel5Forces;
            const channel6 = repetition.channel6Forces;
            const channel7 = repetition.channel7Forces;
            const channel8 = repetition.channel8Forces;
            const channel9 = repetition.channel9Forces;
            const channel10 = repetition.channel10Forces;
            const channel11 = repetition.channel11Forces;
            const channel12 = repetition.channel12Forces;
            const channel13 = repetition.channel13Forces;
            const channel14 = repetition.channel14Forces;
            const channel15 = repetition.channel15Forces;
            const channel16 = repetition.channel16Forces;
            const channel17 = repetition.channel17Forces;


            deltas.forEach((_, index) => {
                csvContent += `${repetitionIndex + 1},${repetition.deviceType ?? ''},${repetition.bodyPart ?? ''},${repetition.bodyPartSide ?? ''},${deltas[index]?.toFixed(5) ?? 0} ,${channel1[index]?.toFixed(5) ?? 0},${channel2[index]?.toFixed(5) ?? 0},${channel3[index]?.toFixed(5) ?? 0},${channel4[index]?.toFixed(5) ?? 0},${channel5[index]?.toFixed(5) ?? 0},${channel6[index]?.toFixed(5) ?? 0},${channel7[index]?.toFixed(5) ?? 0},${channel8[index]?.toFixed(5) ?? 0},${channel9[index]?.toFixed(5) ?? 0},${channel10[index]?.toFixed(5) ?? 0},${channel11[index]?.toFixed(5) ?? 0},${channel12[index]?.toFixed(5) ?? 0},${channel13[index]?.toFixed(5) ?? 0},${channel14[index]?.toFixed(5) ?? 0},${channel15[index]?.toFixed(5) ?? 0},${channel16[index]?.toFixed(5) ?? 0},${channel17[index]?.toFixed(5) ?? 0}\n`;
            });
        });

        return csvContent;
    }

    /**
     * Renders the sensors that took part in an activity as "serialCode (firmware)", one per sensor.
     * The firmware falls back to "?" the same way the server side CSV export does. Older records
     * carry neither field, so an empty list is shown as a dash.
     */
    formatSensors(sensors: ActivitySensorDto[]): string {
        if (!sensors || sensors.length === 0) {
            return '-';
        }

        return sensors
            .filter(sensor => !TextUtils.isEmpty(sensor?.serialCode))
            .map(sensor => `${sensor.serialCode} (${TextUtils.isEmpty(sensor.deviceFirmware) ? '?' : sensor.deviceFirmware})`)
            .join(', ') || '-';
    }

    /**
     * An activity is recorded in one session, so normally there is a single app version. More than
     * one is still possible in the data, so show them all rather than picking one.
     */
    formatAppVersions(appVersions: string[]): string {
        if (!appVersions || appVersions.length === 0) {
            return '-';
        }

        return appVersions.filter(version => !TextUtils.isEmpty(version)).join(', ') || '-';
    }

    formatWord(input: string): string {
        // Split the string into words using "_" as the separator
        const words = input.split('_');

        // Capitalize the first letter of each word
        const formattedWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

        // Join the words with whitespace
        return  formattedWords.join(' ');
    }
}
